import React, { useState, useEffect } from 'react'
import MainContainer from '../UI/MainContainer'
import TableContainer from '../UI/TableContainer'
import TableSection from '../UI/TableSection'
import TableHeader from '../UI/TableHeader'
import CreateNewContact from './CreateNewContact'
import EditContact from './EditContact'
import ContactActionModal from './ContactActionModal'
import SmConfirmModal from '../UI/SmConfirmModal'
import ContactItem from './ContactItem'
import { useCreateContactModal, useEditContactModal, useDeleteContactModal } from './Shared/hooks'
import { AnalyticEvent } from '../analytics/GoogleAnalytics'
import { api } from '../Controllers/api'
import { getContacts, createContact, editContact } from './Store/actions'
import { Contact, ContactInput, ContactsProps } from './Types/types'
import { bindActionCreators, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { setSnackBarCopy, toggleSnackBar } from '../UI/Store/actions'
import { useHistory } from 'react-router-dom'
import { ApplicationState } from 'Store/intialState'
import TableTopSection from 'UI/TableTopSection'

const Contacts: React.FC<ContactsProps> = ({
  contacts,
  snackBarCopy,
  snackBarVisible,
  getContactsData,
  createContact,
  editContactData,
  setSnackBarCopy,
  toggleSnackBar
}) => {
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchedValue, setSearchedValue] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState<boolean>(false)
  const [contactList, setContactList] = useState<Contact[]>(contacts.contactList)
  const { createNewContactModal, handleCreateNewContactModal } = useCreateContactModal()
  const { editContactModal, handleEditContactModal } = useEditContactModal()
  const { contactDeleteModal, handleDeleteContactModal } = useDeleteContactModal()

  const [currentContactDetail, setCurrentContatDetail] = useState<Contact | undefined>()
  const history = useHistory()

  const handleCreateContact = (values: ContactInput) => {
    AnalyticEvent('Contacts', 'CreateContactButtonPressed')
    setSnackBarCopy('create')
    createContact(values)
    handleContactActionModal()
  }

  const handleContactActionModal = () => {
    toggleSnackBar(true)
    setTimeout(() => {
      toggleSnackBar(false)
    }, 2500)
  }

  const editContact = (newContactValues: ContactInput, _id: string) => {
    AnalyticEvent('Contacts', 'EditContactSaveButtonPressed')
    setSnackBarCopy('edit')
    editContactData(newContactValues, _id)
    handleContactActionModal()
  }

  const contactDeleteModalToggle = (_id?: string) => {
    const contact = contactList.find((contact: Contact) => contact._id === _id)
    setCurrentContatDetail(contact)
    handleDeleteContactModal(!contactDeleteModal)
  }

  const editContactModalToggle = (_id?: string) => {
    if (_id) {
      const contact = contactList.find(contact => contact._id === _id)
      setCurrentContatDetail(contact)
    }
    handleEditContactModal()
  }

  const getContacts = () => {
    getContactsData(false)
    setContactList(contacts.contactList)
  }

  const deleteContact = () => {
    const _id = currentContactDetail && currentContactDetail._id
    if (_id) {
      AnalyticEvent('Contacts', 'DeleteContactConfirmButtonPressed')
      setSnackBarCopy('delete')
      api.delete(`contacts/${_id}`).then(data => {
        handleDeleteContactModal(!contactDeleteModal)
        handleContactActionModal()
        getContacts()
      })
    } else {
      handleDeleteContactModal(!contactDeleteModal)
    }
  }

  const search = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchedValue(searchValue)
    history.push(`/app/contacts?search=${searchValue}`)
    let searchList: Contact[] = []
    if (searchValue.length === 0) {
      history.push(`/app/contacts`)
      setSearchFilter(false)
      setContactList(contacts.contactList)
      return
    }
    //eslint-disable-next-line
    contacts.contactList.map((item: Contact) => {
      if (
        item.firstName.toLowerCase().search(searchValue.toLowerCase()) !== -1 ||
        item.lastName.toLowerCase().search(searchValue.toLowerCase()) !== -1 ||
        item.company.toLowerCase().search(searchValue.toLowerCase()) !== -1
      ) {
        searchList = searchList.concat(item)
      }
    })
    setContactList(searchList)
    setSearchFilter(true)
  }

  const clearSearch = () => {
    setSearchValue('')
    setSearchedValue('')
    history.push(`/app/contacts`)
    setContactList(contacts.contactList)
    setSearchFilter(false)
  }

  useEffect(() => {
    getContactsData(true)
  }, []) //eslint-disable-line

  useEffect(() => {
    if (contacts.contactList && searchValue === '' && searchedValue === '') {
      setContactList(contacts.contactList)
    }
  }, [contacts.loading, contacts.contactList, searchValue, searchedValue])

  return (
    <MainContainer>
      <TableContainer>
        <TableTopSection
          title="Contacts"
          loading={contacts.loading}
          searchString={
            !contacts.loading ? (
              <p style={{ fontSize: '0.875rem', color: '#9EA0A5', padding: '0 15px' }}>
                {!searchFilter &&
                  contactList &&
                  searchedValue.length === 0 &&
                  `${contactList.length} Contacts`}
                {!contactList && `0 Contacts`}
                {searchFilter &&
                  `${contactList.length}  ${
                    contactList.length > 1 ? 'Contacts' : 'Contact'
                  } found for '${searchedValue}'`}
              </p>
            ) : (
              undefined
            )
          }
          searchSubmit={e => search(e)}
          searchXButtonPress={clearSearch}
          searchValue={searchValue}
          setSearchValue={v => setSearchValue(v)}
          handlePlusIconClick={handleCreateNewContactModal}
        />

        <TableSection>
          <thead>
            <tr>
              {['NAME', 'COMPANY', 'E-MAIL', 'PHONE', ''].map(item => (
                <TableHeader title={item} key={item} />
              ))}
            </tr>
          </thead>
          <tbody>
            {!contacts.loading && contacts.contactList && contactList ? (
              contactList.map((item, i) => (
                <ContactItem
                  item={item}
                  i={i}
                  contactDeleteModalToggle={contactDeleteModalToggle}
                  editContactModalToggle={editContactModalToggle}
                  key={item._id}
                />
              ))
            ) : (
              <tr></tr>
            )}
          </tbody>
        </TableSection>
      </TableContainer>
      {createNewContactModal && (
        <CreateNewContact
          createNewContactModalToggle={handleCreateNewContactModal}
          createContact={handleCreateContact}
        />
      )}
      {editContactModal && currentContactDetail && (
        <EditContact
          editContactModalToggle={editContactModalToggle}
          editContact={editContact}
          editContactDetail={currentContactDetail}
        />
      )}
      {snackBarVisible && <ContactActionModal contactAction={snackBarCopy} />}
      {contactDeleteModal && (
        <SmConfirmModal
          heading="Delete Contact"
          text={`Are you sure you want to delete contact ${currentContactDetail?.firstName} ${currentContactDetail?.lastName}?`}
          onCloseClick={contactDeleteModalToggle}
          onOKClick={deleteContact}
        />
      )}
    </MainContainer>
  )
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    contacts: state.contacts,
    snackBarCopy: state.ui.snackBarCopy,
    snackBarVisible: state.ui.snackBarVisible
  }
}

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getContactsData: getContacts,
      createContact,
      editContactData: editContact,
      setSnackBarCopy,
      toggleSnackBar
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(Contacts)
